import { CvSuccessScreen } from 'components/cv-success';
import { ErrorScreen } from 'components/screens/parts/ErrorScreen';
import { getTrans } from 'modules/i18n';
import { getCvReviewTimeline } from '../cv-builder/components/cvReviewTimeline';
import { GuestInfoScreen } from './GuestInfoScreen';
import { GuestRedeemState } from './GuestSessionContext';

/** The non-loading, non-active redeem outcomes that render a status/terminal screen. */
export type GuestStatusState = Exclude<GuestRedeemState, 'loading' | 'active'>;

/**
 * Terminal screen for the guest flow, driven by the redeem state. Three treatments:
 *
 *  - `submitted` is a success — the same success screen + animation as the authenticated
 *    flow, now including the review timeline so the applicant learns what happens next
 *    instead of being told "you can close this page".
 *  - `missing-token` is recoverable and loses nothing, so it gets the informational
 *    treatment rather than the error illustration.
 *  - `expired` / `invalid` / `unavailable` are genuine failures and keep the ErrorScreen.
 *
 * Copy comes from `guestCv.status.<state>.*`.
 */
export const GuestStatusScreen = ({ state }: { state: GuestStatusState }) => {
  const title = getTrans(`guestCv.status.${state}.title`);
  const description1 = getTrans(`guestCv.status.${state}.body1`);
  const description2 = getTrans(`guestCv.status.${state}.body2`);

  if (state === 'submitted') {
    return (
      <CvSuccessScreen
        title={title}
        description1={description1}
        description2={description2}
        timeline={getCvReviewTimeline()}
      />
    );
  }

  if (state === 'missing-token') {
    return (
      <GuestInfoScreen
        title={title}
        description1={description1}
        description2={description2}
      />
    );
  }

  return (
    <ErrorScreen
      title={title}
      description1={description1}
      description2={description2}
    />
  );
};
